import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../../components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import { useBookmarks } from '@/hooks/useBookmarks';

const quickActions = [
  {
    id: 'search',
    label: '간병인 찾기',
    icon: 'ri-search-line',
    color: '#2E7D6B',
    bg: '#E8F5E9',
    path: '/app/patient/search',
  },
  {
    id: 'request',
    label: '간병 신청하기',
    icon: 'ri-file-list-3-line',
    color: '#F57C00',
    bg: '#FFF3E0',
    path: '/app/care-request',
  },
  {
    id: 'history',
    label: '간병 이력',
    icon: 'ri-history-line',
    color: '#7B1FA2',
    bg: '#F3E5F5',
    path: '/app/patient/history',
  },
  {
    id: 'insurance',
    label: '보험서류 요청',
    icon: 'ri-file-shield-2-line',
    color: '#1976D2',
    bg: '#E3F2FD',
    path: '/app/insurance-doc',
  },
];

function BookmarkedSection() {
  const navigate = useNavigate();
  const auth = useAppAuth(false);
  const { bookmarkedIds } = useBookmarks();
  const [cgList, setCgList] = useState<{
    userId: string;
    name: string;
    avatarUrl: string | null;
    hourlyRate: number;
    experienceYears: number;
    ratingAvg: number;
  }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!auth.userId || bookmarkedIds.size === 0) {
      setCgList([]);
      return;
    }
    setLoading(true);
    const ids = Array.from(bookmarkedIds);
    Promise.all([
      supabase.from('caregiver_profiles').select('user_id, hourly_rate, experience_years, rating_avg').in('user_id', ids),
      supabase.from('profiles').select('id, name, avatar_url').in('id', ids),
    ]).then(([cgRes, profileRes]) => {
      const profileMap = new Map((profileRes.data || []).map((p: any) => [p.id, p]));
      const mapped = (cgRes.data || []).map((cg: any) => {
        const p = profileMap.get(cg.user_id);
        return {
          userId: cg.user_id,
          name: p?.name || '이름 없음',
          avatarUrl: p?.avatar_url || null,
          hourlyRate: cg.hourly_rate || 15000,
          experienceYears: cg.experience_years || 0,
          ratingAvg: Number(cg.rating_avg) || 0,
        };
      });
      setCgList(mapped);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [auth.userId, bookmarkedIds]);

  if (bookmarkedIds.size === 0) return null;

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
          <i className="ri-bookmark-fill text-amber-500" />
          찜한 간병인
          <span className="text-xs font-medium text-gray-400">({bookmarkedIds.size})</span>
        </h2>
      </div>
      {loading ? (
        <div className="flex justify-center py-4">
          <div className="w-5 h-5 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {cgList.map((cg) => (
            <button
              key={cg.userId}
              onClick={() => navigate(`/app/caregiver/${cg.userId}`)}
              className="w-full text-left bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-3 cursor-pointer hover:shadow-sm transition-shadow"
            >
              <div className="w-11 h-11 rounded-xl bg-[#E8F5E9] flex items-center justify-center shrink-0">
                {cg.avatarUrl ? (
                  <img src={cg.avatarUrl} alt={cg.name} className="w-full h-full rounded-xl object-cover" />
                ) : (
                  <i className="ri-user-3-line text-lg text-[#2E7D6B]/40" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-gray-900">{cg.name}</h3>
                <p className="text-xs text-[#2E7D6B] font-semibold">
                  {cg.hourlyRate.toLocaleString()}원/시간 · 경력 {cg.experienceYears}년
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <i className="ri-star-fill text-amber-400 text-[10px]" />
                <span className="font-bold text-gray-700">{cg.ratingAvg > 0 ? cg.ratingAvg.toFixed(1) : '-'}</span>
              </div>
              <i className="ri-arrow-right-s-line text-gray-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppPatientHomePage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [profile, setProfile] = useState<{ name: string } | null>(null);
  const { recent } = useRecentlyViewed();

  const [matchCounts, setMatchCounts] = useState({ requested: 0, ongoing: 0, completed: 0 });
  const [recommended, setRecommended] = useState<{
    userId: string;
    name: string;
    gender: string;
    age: number;
    avatarUrl: string | null;
    hourlyRate: number;
    experienceYears: number;
    introduction: string;
    availableRegions: string[];
    certificates: string[];
    ratingAvg: number;
    totalMatches: number;
  }[]>([]);

  useEffect(() => {
    if (!auth.userId) return;
    loadProfile();
    loadMatchCounts();
    loadRecommended();
  }, [auth.userId]);

  async function loadProfile() {
    const { data } = await supabase.from('profiles').select('name').eq('id', auth.userId).maybeSingle();
    if (data) setProfile(data);
  }

  async function loadMatchCounts() {
    try {
      const { data } = await supabase
        .from('match_requests')
        .select('status')
        .eq('requester_id', auth.userId);

      let requested = 0, ongoing = 0, completed = 0;
      (data || []).forEach((m) => {
        if (m.status === 'pending') requested++;
        else if (m.status === 'accepted') ongoing++;
        else if (m.status === 'completed') completed++;
      });
      setMatchCounts({ requested, ongoing, completed });
    } catch (err) {
      console.error('Failed to load match counts:', err);
    }
  }

  async function loadRecommended() {
    try {
      const { data: cgList } = await supabase
        .from('caregiver_profiles')
        .select('*')
        .eq('is_active', true)
        .order('rating_avg', { ascending: false })
        .limit(3);

      if (!cgList || cgList.length === 0) return;

      const userIds = cgList.map((cg) => cg.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name, gender, birth_date, avatar_url')
        .in('id', userIds);
      const profileMap = new Map((profiles || []).map((p) => [p.id, p]));

      const mapped = cgList.map((cg) => {
        const p = profileMap.get(cg.user_id);
        return {
          userId: cg.user_id,
          name: p?.name || '이름 없음',
          gender: p?.gender || '',
          age: p?.birth_date ? new Date().getFullYear() - new Date(p.birth_date).getFullYear() : 0,
          avatarUrl: p?.avatar_url || null,
          hourlyRate: cg.hourly_rate || 15000,
          experienceYears: cg.experience_years || 0,
          introduction: cg.introduction || '',
          availableRegions: cg.available_regions || [],
          certificates: cg.certificates || [],
          ratingAvg: Number(cg.rating_avg) || 0,
          totalMatches: cg.total_matches || 0,
        };
      });
      setRecommended(mapped);
    } catch (err) {
      console.error('Failed to load recommended:', err);
    }
  }

  if (auth.loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="mb-5">
          <h1 className="text-[22px] font-extrabold text-gray-900 leading-tight">
            {profile ? `${profile.name}님,` : ''}
            <br />
            <span className="text-[#2E7D6B]">믿을 수 있는 간병인</span>을
            <br />
            찾아보세요
          </h1>
          <p className="text-sm text-gray-400 mt-1.5">검증된 간병인이 도와드립니다</p>
        </div>

        {/* 빠른 액션 버튼 그리드 (2x2) */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => navigate(action.path)}
              className="bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:shadow-sm transition-all active:scale-[0.98]"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: action.bg }}
              >
                <i className={`${action.icon} text-lg`} style={{ color: action.color }} />
              </div>
              <h3 className="text-sm font-bold text-gray-900">{action.label}</h3>
            </button>
          ))}
        </div>

        {/* 간병인 검색 메인 CTA */}
        <button
          onClick={() => navigate('/app/patient/search')}
          className="w-full rounded-2xl p-5 mb-5 text-white text-left cursor-pointer hover:opacity-95 transition-all active:scale-[0.99]"
          style={{ backgroundColor: '#2E7D6B' }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-white/70 mb-1">지금 바로 시작하기</p>
              <p className="text-lg font-extrabold">간병인 검색하기</p>
              <p className="text-sm text-white/80 mt-1">지역, 경력, 시급으로 쉽게 찾기</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <i className="ri-arrow-right-line text-xl" />
            </div>
          </div>
        </button>

        {/* 나의 매칭 현황 */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-extrabold text-gray-900">나의 매칭 현황</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/app/my-care-requests')}
                className="text-xs text-[#F57C00] font-bold cursor-pointer flex items-center gap-0.5"
              >
                내 간병요청 <i className="ri-arrow-right-s-line" />
              </button>
              <button
                onClick={() => navigate('/app/patient/history')}
                className="text-xs text-[#7B1FA2] font-bold cursor-pointer flex items-center gap-0.5"
              >
                간병이력 <i className="ri-arrow-right-s-line" />
              </button>
              <button
                onClick={() => navigate('/app/matching')}
                className="text-xs text-[#2E7D6B] font-bold cursor-pointer flex items-center gap-0.5"
              >
                전체보기 <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-3 divide-x divide-gray-50">
              {[
                { label: '매칭 요청', count: matchCounts.requested, icon: 'ri-send-plane-line', color: '#F57C00', bg: '#FFF3E0' },
                { label: '진행 중', count: matchCounts.ongoing, icon: 'ri-loop-left-line', color: '#1976D2', bg: '#E3F2FD' },
                { label: '완료', count: matchCounts.completed, icon: 'ri-check-double-line', color: '#2E7D6B', bg: '#E8F5E9' },
              ].map((stat) => (
                <div key={stat.label} className="p-4 text-center">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2"
                    style={{ backgroundColor: stat.bg }}
                  >
                    <i className={`${stat.icon} text-sm`} style={{ color: stat.color }} />
                  </div>
                  <p className="text-lg font-extrabold text-gray-900">{stat.count}</p>
                  <p className="text-[11px] text-gray-400 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 추천 간병인 */}
        {recommended.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-extrabold text-gray-900">추천 간병인</h2>
              <button
                onClick={() => navigate('/app/patient/search')}
                className="text-xs text-[#2E7D6B] font-bold cursor-pointer flex items-center gap-0.5"
              >
                전체보기 <i className="ri-arrow-right-s-line" />
              </button>
            </div>

            <div className="space-y-3">
              {recommended.map((cg) => (
                <button
                  key={cg.userId}
                  onClick={() => navigate(`/app/caregiver/${cg.userId}`)}
                  className="w-full text-left bg-white rounded-2xl border border-gray-100 p-4 cursor-pointer hover:shadow-sm transition-shadow"
                >
                  <div className="flex gap-3">
                    {cg.avatarUrl ? (
                      <img src={cg.avatarUrl} alt={cg.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-[#E8F5E9] flex items-center justify-center shrink-0">
                        <i className="ri-user-3-line text-xl text-[#2E7D6B]/40" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-sm font-bold text-gray-900">{cg.name}</h3>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-500 font-medium">
                          {cg.gender} · {cg.age}세
                        </span>
                        <span className="ml-auto flex items-center gap-0.5 text-xs font-bold text-amber-500">
                          <i className="ri-star-fill text-[10px]" />{cg.ratingAvg > 0 ? cg.ratingAvg.toFixed(1) : '-'}
                        </span>
                      </div>
                      <p className="text-xs text-[#2E7D6B] font-semibold">
                        {cg.hourlyRate.toLocaleString()}원/시간 · 경력 {cg.experienceYears}년
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">{cg.introduction}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {cg.certificates.slice(0, 3).map((cert) => (
                          <span key={cert} className="text-[10px] px-2 py-0.5 rounded-full bg-[#f0f9f6] text-[#2E7D6B] font-medium">{cert}</span>
                        ))}
                        {cg.certificates.length > 3 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 font-medium">+{cg.certificates.length - 3}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                    <span className="text-[10px] text-gray-400">
                      <i className="ri-map-pin-line mr-0.5" />{cg.availableRegions[0] || '지역 미입력'}{cg.availableRegions.length > 1 ? ` 외 ${cg.availableRegions.length - 1}곳` : ''}
                    </span>
                    <span className="text-[10px] text-gray-400">{cg.totalMatches}회 매칭</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 최근 본 간병인 */}
        {recent.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-extrabold text-gray-900">최근 본 간병인</h2>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {recent.slice(0, 5).map((cg) => (
                <button
                  key={cg.userId}
                  onClick={() => navigate(`/app/caregiver/${cg.userId}`)}
                  className="shrink-0 w-28 bg-white rounded-2xl border border-gray-100 p-3 text-center cursor-pointer hover:shadow-sm transition-shadow"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#E8F5E9] flex items-center justify-center mx-auto mb-2">
                    {cg.avatarUrl ? (
                      <img src={cg.avatarUrl} alt={cg.name} className="w-full h-full rounded-xl object-cover" />
                    ) : (
                      <i className="ri-user-3-line text-xl text-[#2E7D6B]/40" />
                    )}
                  </div>
                  <p className="text-xs font-bold text-gray-900 truncate">{cg.name}</p>
                  <p className="text-[10px] text-[#2E7D6B] font-semibold mt-0.5">{cg.hourlyRate.toLocaleString()}원</p>
                  <div className="flex items-center justify-center gap-0.5 mt-1">
                    <i className="ri-star-fill text-amber-400 text-[10px]" />
                    <span className="text-[10px] text-gray-500">{cg.ratingAvg > 0 ? cg.ratingAvg.toFixed(1) : '-'}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 찜한 간병인 */}
        <BookmarkedSection />

        {/* 서비스 이용 안내 */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h2 className="text-sm font-extrabold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-lightbulb-flash-line text-amber-500 text-lg" />
            서비스 이용 안내
          </h2>
          <div className="space-y-4">
            {[
              {
                step: '01',
                title: '조건에 맞는 간병인 검색',
                desc: '지역, 경력, 시급, 자격증으로 필터링하여 원하는 간병인을 찾아보세요.',
                icon: 'ri-search-line',
              },
              {
                step: '02',
                title: '프로필 확인 후 매칭 신청',
                desc: '간병인의 상세 프로필을 확인하고 매칭을 신청하세요. 상대방이 수락하면 연락처가 공개됩니다.',
                icon: 'ri-user-heart-line',
              },
              {
                step: '03',
                title: '간병 완료 후 직접 정산',
                desc: '간병비는 간병인과 직접 정산합니다. 회사는 간병 완료 후 중개 수수료를 수취합니다.',
                icon: 'ri-exchange-funds-line',
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#f0f9f6] flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-sm text-[#2E7D6B]`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-extrabold text-[#2E7D6B] bg-[#f0f9f6] px-1.5 py-0.5 rounded-md">
                      {item.step}
                    </span>
                    <h3 className="text-sm font-bold text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}